import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Check, ShieldCheck, Lock } from "lucide-react";
import { toast } from "sonner";
import { Layout } from "../components/Layout";
import { getProgram, financeText } from "../lib/programs";
import api from "../lib/api";

export default function ProgramDetail() {
  const { slug } = useParams();
  const p = getProgram(slug);
  const [busy, setBusy] = useState(false);

  if (!p) return <Layout><div className="max-w-[1400px] mx-auto px-5 py-32 text-eon-muted">Program not found. <Link to="/programs" className="text-eon-gold">Back to programs</Link></div></Layout>;

  const buy = async (deposit = false) => {
    setBusy(true);
    try {
      const { data } = await api.post("/payments/checkout", { kind: "program", program_slug: p.slug, deposit, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not start checkout");
      setBusy(false);
    }
  };

  const depositEligible = p.price >= 10000;
  const depositAmount = Math.round(p.price * 0.25);

  return (
    <Layout>
      <section className="relative min-h-[380px] lg:min-h-[460px] overflow-hidden border-b border-eon-border">
        <img src={p.image} alt={p.title} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-eon-bg via-eon-bg/60 to-eon-bg/20" />
        <div className="relative max-w-[1400px] mx-auto px-5 lg:px-8 h-full flex flex-col justify-end py-12">
          <Link to="/programs" className="inline-flex items-center gap-2 text-sm text-eon-text/70 hover:text-eon-gold transition-colors mb-6"><ArrowLeft size={16} /> All Programs</Link>
          <div className="eon-label mb-3">{p.category === "B2B" ? "For Business" : "For Owners"}</div>
          <h1 className="eon-heading text-4xl sm:text-5xl lg:text-6xl uppercase max-w-3xl">{p.title}</h1>
          <p className="mt-4 text-lg text-eon-gold font-mono">{p.priceFrom}</p>
          <p className="mt-1 text-sm text-eon-text/70" data-testid="program-finance">{financeText(p)}</p>
        </div>
      </section>

      <div className="max-w-[1400px] mx-auto px-5 lg:px-8 py-16 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-7 space-y-12">
          <p className="text-lg text-eon-text/85 leading-relaxed">{p.summary}</p>

          <div>
            <div className="eon-label mb-5">What's Included</div>
            <div className="grid sm:grid-cols-2 gap-4">
              {p.features.map((f) => (
                <div key={f} className="flex items-start gap-3 bg-eon-surface border border-eon-border p-4">
                  <Check size={18} className="text-eon-gold mt-0.5 shrink-0" />
                  <span className="text-sm text-eon-text/85">{f}</span>
                </div>
              ))}
            </div>
          </div>

          {p.process?.length > 0 && (
            <div>
              <div className="eon-label mb-5">How It Works</div>
              <div className="space-y-3">
                {p.process.map((step, i) => (
                  <div key={step} className="flex items-center gap-4 border-l-2 border-eon-gold/40 pl-4 py-1">
                    <span className="font-display text-2xl font-black text-eon-gold/60 w-8">{i + 1}</span>
                    <span className="text-eon-text/85">{step}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="lg:col-span-5">
          <div className="eon-card p-8 lg:sticky lg:top-28" data-testid="program-buy-card">
            <div className="eon-label mb-2">Price</div>
            <div className="font-display text-4xl font-black text-eon-text">${p.price.toLocaleString()}</div>
            <p className="text-xs text-eon-muted mt-1" data-testid="program-finance">{financeText(p)}</p>
            <button onClick={() => buy(false)} disabled={busy}
              className="w-full mt-6 bg-eon-gold text-white font-bold uppercase tracking-wider py-4 hover:bg-eon-goldhover transition-colors disabled:opacity-40 flex items-center justify-center gap-2"
              data-testid="program-buy-btn">
              <Lock size={16} /> {busy ? "Starting checkout..." : "Buy Now"}
            </button>
            {depositEligible && (
              <button onClick={() => buy(true)} disabled={busy}
                className="w-full mt-3 bg-white border border-eon-gold text-eon-gold font-bold uppercase tracking-wider py-3.5 hover:bg-eon-gold hover:text-white transition-colors disabled:opacity-40 flex items-center justify-center gap-2"
                data-testid="program-deposit-btn">
                Pay 25% Deposit — ${depositAmount.toLocaleString()}
              </button>
            )}
            {depositEligible && (
              <p className="text-xs text-eon-muted text-center mt-2">
                Put ${depositAmount.toLocaleString()} down now to secure your spot; remaining ${(p.price - depositAmount).toLocaleString()} billed later.
              </p>
            )}
            <div className="flex items-center gap-2 justify-center mt-3 text-xs text-eon-muted">
              <ShieldCheck size={14} className="text-eon-gold" /> Secure checkout — pay instantly by card
            </div>
            <p className="text-xs text-eon-muted text-center mt-4 border-t border-eon-border pt-4">
              Questions before you buy? Call <a href="tel:8176929093" className="text-eon-gold">817-692-9093</a>
            </p>
          </div>
        </div>
      </div>
    </Layout>
  );
}
